import {
  getWaxUpTypeSelection,
  isArchScopedItem,
  isToothScopedItem,
  ItemTypeEnum,
  MaterialCharacteristics,
  Product,
  ProductTypeEnum,
  type ItemType,
} from "@jasper/client-models";
import { getItemTypeLabel } from "@jasper/shared";
import {
  useItemMaterials,
  useItemShades,
  useScanPostCatalog,
} from "@jasper/use-cases";
import { ActionIcon, Loader, Text } from "@mantine/core";
import { Grid, Group, Stack } from "@mantine/core";
import { Fragment, useMemo } from "react";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { LabelledValue } from "./components/LabelledValue";

export const ProductDetails = ({
  product,
  onEdit,
  onDelete,
}: {
  product: Product;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const { t } = useTranslation("product-workflows");

  const archItems = useMemo(
    () => product.items.filter(isArchScopedItem),
    [product.items]
  );
  const toothItems = useMemo(
    () => product.items.filter(isToothScopedItem),
    [product.items]
  );

  const scopeLabel = archItems.length
    ? archItems.map(item => t(`arches.${item.arch}`)).join(", ")
    : `${product.teeth.length > 1 ? "Dents" : "Dent"} ${product.teeth.join(
        ", "
      )}`;

  const uniqueMaterialsCharacteristics: {
    characteristics: MaterialCharacteristics;
    itemType: ItemType;
    teeth: number[];
  }[] = useMemo(() => {
    const map = toothItems.reduce(
      (acc, item) => {
        const material = item.materialCharacteristics?.material;

        if (!material) {
          return acc;
        }

        const shadeCharacteristics =
          item.materialCharacteristics &&
          "shadeCharacteristics" in item.materialCharacteristics
            ? item.materialCharacteristics.shadeCharacteristics
            : undefined;

        if (!shadeCharacteristics && item.materialCharacteristics) {
          const key = `${item.type}-${material}`;

          if (!acc[key]) {
            acc[key] = {
              characteristics: item.materialCharacteristics,
              itemType: item.type,
              teeth: [item.tooth],
            };
          } else {
            acc[key].teeth.push(item.tooth);
          }
          return acc;
        }

        if (!shadeCharacteristics || !item.materialCharacteristics) {
          return acc;
        }

        const key = shadeCharacteristics.isMultiShade
          ? `${item.type}-${material}-${shadeCharacteristics.shadeType}-${shadeCharacteristics.baseShade}-${shadeCharacteristics.incisalShade}-${shadeCharacteristics.gingivalShade}`
          : `${item.type}-${material}-${shadeCharacteristics.shadeType}-${shadeCharacteristics.shade}`;

        if (!acc[key]) {
          acc[key] = {
            characteristics: item.materialCharacteristics,
            itemType: item.type,
            teeth: [item.tooth],
          };
        } else {
          acc[key].teeth.push(item.tooth);
        }

        return acc;
      },
      {} as Record<
        string,
        {
          characteristics: MaterialCharacteristics;
          itemType: ItemType;
          teeth: number[];
        }
      >
    );

    return Object.values(map).sort((a, b) => a.teeth[0] - b.teeth[0]);
  }, [toothItems]);

  const isMultiCharacteristics = uniqueMaterialsCharacteristics.length > 1;

  const itemShadesIds = useMemo(() => {
    return uniqueMaterialsCharacteristics.flatMap(({ characteristics }) => {
      const shadeCharacteristics =
        "shadeCharacteristics" in characteristics
          ? characteristics.shadeCharacteristics
          : undefined;
      if (!shadeCharacteristics) {
        return [];
      }
      if (shadeCharacteristics.isMultiShade) {
        return [
          shadeCharacteristics.baseShade,
          shadeCharacteristics.incisalShade,
          shadeCharacteristics.gingivalShade,
        ];
      } else {
        return [shadeCharacteristics.shade];
      }
    });
  }, [uniqueMaterialsCharacteristics]);

  const uniqMaterialIds = useMemo(() => {
    const materialIds = uniqueMaterialsCharacteristics.map(
      ({ characteristics }) => characteristics.material
    );
    const uniqMaterialIds = Array.from(new Set(materialIds));
    return uniqMaterialIds;
  }, [uniqueMaterialsCharacteristics]);

  const {
    scanPostCatalog,
    isLoading: isLoadingScanPosts,
    error: errorScanPosts,
  } = useScanPostCatalog();

  const inlayCoreCharacteristics =
    product.type === ProductTypeEnum.inlayCore
      ? product.items.find(item => item.type === ItemTypeEnum.inlayCore)
          ?.inlayCoreCharacteristics
      : undefined;

  const waxUpType =
    product.type === ProductTypeEnum.waxUp
      ? getWaxUpTypeSelection(toothItems)
      : undefined;

  const scanPostLabel = inlayCoreCharacteristics
    ? (scanPostCatalog.scanPosts.find(
        scanPost => scanPost.value === inlayCoreCharacteristics.scanPost
      )?.label ??
      scanPostCatalog.types.find(
        scanPostType => scanPostType.value === inlayCoreCharacteristics.scanPost
      )?.label ??
      inlayCoreCharacteristics.scanPost)
    : undefined;

  const {
    itemShades,
    isLoading: isLoadingShades,
    error: errorShades,
  } = useItemShades({ ids: itemShadesIds });
  const {
    itemMaterials,
    isLoading: isLoadingMaterials,
    error: errorMaterials,
  } = useItemMaterials({ ids: uniqMaterialIds });

  return (
    <Stack w="100%" gap="xs">
      <Grid w="100%">
        <Grid.Col span={6}>
          <Group>
            <Text fw={500}>
              {/* TODO: Mettre en place i18n sur la webapp ? - Si ce commentaire est sur ma MR c'est que j'ai oublié ce point 😁 */}
              {getItemTypeLabel(product.type as any)} ({scopeLabel})
            </Text>
          </Group>
        </Grid.Col>

        <Grid.Col span={2} offset={4}>
          <Group justify="flex-end">
            <ActionIcon variant="subtle" color="primary" onClick={onEdit}>
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon variant="subtle" color="primary" onClick={onDelete}>
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Grid.Col>
      </Grid>

      <Grid>
        {inlayCoreCharacteristics && (
          <>
            <Grid.Col span={6}>
              <LabelledValue
                label="Clavette"
                text={inlayCoreCharacteristics.hasClavette ? "Oui" : "Non"}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              {isLoadingScanPosts || errorScanPosts ? (
                <LabelledValue label="Scan post">
                  {isLoadingScanPosts ? (
                    <Loader size="xs" />
                  ) : (
                    <Text c="red">Erreur lors du chargement</Text>
                  )}
                </LabelledValue>
              ) : (
                <LabelledValue label="Scan post" text={scanPostLabel ?? ""} />
              )}
            </Grid.Col>
          </>
        )}
        {waxUpType && (
          <Grid.Col span={6}>
            <LabelledValue
              label="Type"
              text={t(`steps.waxUpType.${waxUpType}`)}
            />
          </Grid.Col>
        )}
        {uniqueMaterialsCharacteristics.map(
          ({ characteristics, itemType, teeth }, index) => {
            const material = itemMaterials.find(
              material => material.id === characteristics.material
            );
            const shadeCharacteristics =
              "shadeCharacteristics" in characteristics
                ? characteristics.shadeCharacteristics
                : undefined;

            const isMultiItemCharacteristics =
              new Set(
                product.items
                  .filter(item => !!item.materialCharacteristics)
                  .map(item => item.type)
              ).size > 1;

            return (
              <Fragment key={`${itemType}-${index}`}>
                {isMultiItemCharacteristics && (
                  <Grid.Col span={12}>
                    <Text fw={500}>{getItemTypeLabel(itemType as any)}</Text>
                  </Grid.Col>
                )}
                {isMultiCharacteristics && (
                  <Grid.Col span={2}>
                    <LabelledValue label="Dents" text={teeth.join(", ")} />
                  </Grid.Col>
                )}
                <Grid.Col span={isMultiCharacteristics ? 5 : 6}>
                  {isLoadingMaterials || errorMaterials ? (
                    <LabelledValue label="Matériau">
                      {isLoadingMaterials ? (
                        <Loader size="xs" />
                      ) : (
                        <Text c="red">Erreur lors du chargement</Text>
                      )}
                    </LabelledValue>
                  ) : (
                    <LabelledValue
                      label="Matériau"
                      text={material?.name || ""}
                    />
                  )}
                </Grid.Col>

                {shadeCharacteristics && (
                  <Grid.Col span={isMultiCharacteristics ? 5 : 6}>
                    {shadeCharacteristics.isMultiShade ? (
                      <LabelledValue label="Teinte">
                        {[
                          {
                            label: "Cervical",
                            shadeId: shadeCharacteristics.gingivalShade,
                          },
                          {
                            label: "Médian",
                            shadeId: shadeCharacteristics.baseShade,
                          },
                          {
                            label: "Incisal",
                            shadeId: shadeCharacteristics.incisalShade,
                          },
                        ].map(({ label, shadeId }) => (
                          <Group gap="xs" justify="space-between" key={label}>
                            <Text fw={250} c="gray" tt="uppercase">
                              {label}
                            </Text>
                            {isLoadingShades || errorShades ? (
                              isLoadingShades ? (
                                <Loader size="xs" />
                              ) : (
                                <Text c="red">Erreur lors du chargement</Text>
                              )
                            ) : (
                              <Text fw={500}>
                                {
                                  itemShades.find(shade => shade.id === shadeId)
                                    ?.name
                                }
                              </Text>
                            )}
                          </Group>
                        ))}
                      </LabelledValue>
                    ) : isLoadingShades || errorShades ? (
                      <LabelledValue label="Teinte">
                        {isLoadingShades ? (
                          <Loader size="xs" />
                        ) : (
                          <Text c="red">Erreur lors du chargement</Text>
                        )}
                      </LabelledValue>
                    ) : (
                      <LabelledValue
                        label="Teinte"
                        text={
                          itemShades.find(
                            shade => shade.id === shadeCharacteristics.shade
                          )?.name || ""
                        }
                      />
                    )}
                  </Grid.Col>
                )}
              </Fragment>
            );
          }
        )}
      </Grid>
    </Stack>
  );
};
